<template>
  <div>
    <DeleteModal
      modal-id="modal-attachment-delete"
      @deleteToggle="deleteItem"
    />

    <!-- <b-modal
      :no-close-on-backdrop="true"
      id="modal-attachment-delete"
      title="Delete Attachment"
      ok-only
      ok-title="Delete"
      @ok="deleteItem"
    >
      <b-card-text>
        <h5>Are You Sure You Want to Remove This Attachment</h5>
        If you want to remove this Attachment, click the button below.
      </b-card-text>
    </b-modal> -->
    <b-card>
      <!-- Header Section -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="mb-0">
          <feather-icon
            icon="PaperclipIcon"
            size="20"
            class="mr-1"
          />
          {{ $t("reception.Patient Attachments") }}
        </h4>

        <b-form-radio-group
          v-model="selected"
          :options="options"
          value-field="item"
          text-field="name"
          disabled-field="notEnabled"
          buttons
          button-variant="outline-primary"
          size="sm"
          @change="attachmentChange"
        />
      </div>

      <!-- Loading State -->
      <template v-if="loading">
        <div class="text-center py-5">
          <b-spinner label="Loading..." />
          <p class="text-muted mt-2">
            {{ $t("common.loading") }}...
          </p>
        </div>
      </template>

      <!-- Attachments Grid -->
      <div
        v-else-if="data && data.length > 0"
        class="attachments-grid mb-4"
      >
        <div
          v-for="(file, index) in data"
          :key="index"
          class="attachment-item"
        >
          <div class="attachment-card">
            <b-button
              v-if="!isHistory"
              variant="danger"
              size="sm"
              class="delete-btn"
              pill
              @click="deleteModel(file)"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
              />
            </b-button>

            <div
              class="attachment-preview"
              @click="openFileInNewTab(file.attachment_url)"
            >
              <b-img
                :src="file.attachment_url"
                fluid
                class="attachment-image"
              />
              <div class="preview-overlay">
                <feather-icon
                  icon="EyeIcon"
                  size="24"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-5"
      >
        <feather-icon
          icon="FileTextIcon"
          size="48"
          class="text-muted mb-2"
        />
        <p class="text-muted mb-0">
          {{ $t("common.no-attachments") }}
        </p>
      </div>

      <!-- Upload Section -->
      <div
        v-if="!isHistory"
        class="upload-section"
      >
        <!-- Scanner Alert Messages - Displayed above buttons -->
        <b-alert
          v-if="scannerAlert"
          :variant="scannerAlertVariant"
          :show="scannerAlert !== null"
          dismissible
          class="scanner-alert-top"
          @dismissed="scannerAlert = null"
        >
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <feather-icon
                :icon="scannerAlertVariant === 'success' ? 'CheckCircleIcon' : 
                       scannerAlertVariant === 'danger' ? 'XCircleIcon' : 
                       scannerAlertVariant === 'warning' ? 'AlertTriangleIcon' : 'InfoIcon'"
                size="18"
                class="mr-1"
              />
              <span>{{ scannerAlert }}</span>
            </div>
            
            <!-- Download Link for Scanner Bridge -->
            <b-button
              v-if="!isScannerConnected && scannerAlertVariant === 'danger'"
              variant="outline-primary"
              size="sm"
              :href="scannerBridgeDownloadUrl"
              download="Scanner-Bridge.exe"
              class="ml-2"
            >
              <feather-icon
                icon="DownloadIcon"
                size="14"
                class="mr-50"
              />
              {{ $t("scanner.download-scanner-bridge") }}
            </b-button>
          </div>
        </b-alert>

        <div class="upload-actions">
          <!-- Scan Button -->
          <DocumentScanner
            ref="documentScanner"
            :show-status="false"
            :show-scan-button="true"
            :inline="false"
            :show-alerts="false"
            class="scanner-component"
            @document-scanned="handleScannedDocument"
            @alert-message="handleScannerAlert"
            @connection-status="handleConnectionStatus"
          />
          
          <!-- Browse Button -->
          <label class="btn btn-outline-primary mb-0 browse-btn">
            <feather-icon
              icon="UploadIcon"
              size="16"
              class="mr-50"
            />
            {{ $t("common.browse") }}
            <b-form-file
              v-model="EditForm.attachment"
              :state="Boolean(EditForm.attachment)"
              :placeholder="$t('common.Drag your file here or click to upload')"
              drop-placeholder="Drop file here..."
              class="d-none"
              @change="handleFileUpload"
            />
          </label>
        </div>
        
        <!-- Selected File Info -->
        <div
          v-if="EditForm.attachment && EditForm.attachment.name"
          class="selected-file-info"
        >
          <feather-icon
            icon="FileIcon"
            size="16"
            class="mr-50"
          />
          <strong>{{ $t("common.File selected") }}:</strong>
          <span class="ml-1">{{ EditForm.attachment.name }}</span>
        </div>

        <!-- Submit Button -->
        <div class="text-center mt-3">
          <b-button
            v-if="$store.state.app.permissions && Array.isArray($store.state.app.permissions) && $store.state.app.permissions.includes('store-patientCardTest')"
            variant="primary"
            :disabled="!EditForm.attachment || EditForm.attachment.length == 0"
            @click="submit()"
          >
            <feather-icon
              icon="UploadCloudIcon"
              size="16"
              class="mr-50"
            />
            {{ $t("common.submit") }}
          </b-button>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
  BFormFile,
  BFormRadio,
  BFormRadioGroup,
  BImg,
  BAlert,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import permissionChecker from "@/mixins/permissionChecker";
import vSelect from "vue-select";
import DeleteModal from "@/components/modals/DeleteModal.vue";
import DocumentScanner from "@/components/common/DocumentScanner.vue";

const AutoComplete = () => import("@/components/common/AutoComplete.vue");

export default {
  components: {
    BImg,
    BFormRadioGroup,
    BFormFile,
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    DeleteModal,
    BSpinner,
    VuePhoneNumberInput,
    AutoComplete,
    vSelect,
    BFormRadio,
    DocumentScanner,
    BAlert,
  },
  mixins: [permissionChecker],

  props: ["patientCardId", "isHistory"],
  data() {
    return {
      take: 10,
      options: [
        { item: "LAB", name: this.$t("lab.lab") },
        { item: "RADIOLOGY", name: this.$t("radiology.radiology") },
      ],
      loading: true,
      isNotValid: false,
      dataCount: 0,
      data: [],
      EditForm: {
        patient_card_id: "",
        attachment: [],
        type: "RADIOLOGY",
        _method: "POST",
      },
      createMode: false,
      errorForm: {
        patient_card_id: "",
        tests: [],
      },
      params: {
        form: {},
        path: "",
      },
      file: null,
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: "",
      sortBy: "id",
      isSortDirDesc: true,
      page: 1,
      selected: "LAB",
      scannerAlert: null,
      scannerAlertVariant: "info",
      isScannerConnected: false,
      scannerBridgeDownloadUrl: "/software-tools/Scanner-Bridge.exe",
    };
  },
  watch: {
    patientCardId: {
      handler(newVal, oldVal) {
        // Only fetch if we have a valid ID and it actually changed from invalid to valid
        if (newVal && newVal !== "-" && newVal !== "" && newVal !== oldVal) {
          this.getAttachments();
        }
      },
      immediate: false,
    },
  },
  mounted() {
    // Only fetch if we have a valid patientCardId on mount
    if (
      this.patientCardId &&
      this.patientCardId !== "-" &&
      this.patientCardId !== ""
    ) {
      this.getAttachments();
    }
  },
  methods: {
    openFileInNewTab(filePath) {
      window.open(filePath, "_blank");
    },
    handleFileUpload() {
      console.log("File uploaded:", this.file);
      // You can process the file here or send it to the server
    },
    handleScannedDocument(doc) {
      try {
        // Convert base64 data URL to Blob
        const base64Data = doc.data.split(',')[1];
        const contentType = doc.data.match(/data:([^;]+);/)[1];
        const byteCharacters = atob(base64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        
        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const extension = contentType.includes('png') ? 'png' : 'jpg';
        const filename = `scanned-${timestamp}.${extension}`;
        
        const file = new File([blob], filename, { 
          type: contentType,
          lastModified: new Date().getTime()
        });
        
        console.log('Scanned file created:', {
          name: file.name,
          size: file.size,
          type: file.type
        });
        
        // Set the file to EditForm and trigger submission
        this.EditForm.attachment = file;
        
        // Auto-submit the scanned document
        this.submit();
      } catch (error) {
        console.error('Error processing scanned document:', error);
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('scanner.error'),
            icon: 'XCircleIcon',
            variant: 'danger',
            text: 'Failed to process scanned document',
          },
        });
      }
    },
    deleteDocs(id) {},
    fileName(file) {},
    handleScannerAlert({ message, variant }) {
      this.scannerAlert = message;
      this.scannerAlertVariant = variant;
      
      // Auto-hide success messages after 5 seconds
      if (variant === 'success') {
        setTimeout(() => {
          this.scannerAlert = null;
        }, 5000);
      }
    },
    handleConnectionStatus(connected) {
      // Track scanner connection status for showing download link
      this.isScannerConnected = connected;
      console.log('Scanner connection status:', connected);
    },

    getIconForVariant() {
      const iconMap = {
        success: 'CheckCircleIcon',
        danger: 'AlertCircleIcon',
        warning: 'AlertTriangleIcon',
        info: 'InfoIcon',
      };
      return iconMap[this.scannerAlertVariant] || 'InfoIcon';
    },

    show(item, mode) {
      if (mode === "create") {
        this.createMode = true;
        this.EditForm = {
          patient_card_id: this.patientCardId,
          tests: [],
          _method: "POST",
        };
      } else {
        this.createMode = false;

        this.EditForm.id = item.id;
        this.EditForm.patient_card_id = this.patientCardId;
        this.EditForm.tests = item.tests;

        this.EditForm._method = "PUT";
      }
      this.$bvModal.show("modal-test-edit");
    },
    showPhoneObj(obj, countryCallingCodeCheck) {
      this.isNotValid = !obj.isValid;
      countryCallingCodeCheck
        ? (this.countryCallingCodeFirst = obj.countryCallingCode)
        : (this.countryCallingCodeSecond = obj.countryCallingCode);
    },
    async getAttachments() {
      if (
        !this.patientCardId ||
        this.patientCardId === "-" ||
        this.patientCardId === ""
      ) {
        this.loading = false;
        this.data = [];
        this.dataCount = 0;
        return;
      }
      this.loading = true;
      // check if there are any null value in the obj we sending

      let url = `/attachment?type=${this.selected}&filter[attachable_id]=${this.patientCardId}`;

      if (this.testFilter) {
        url += `&filter[test_id]=${this.testFilter}`;
      }
      if (this.patientFilter) {
        url += `&filter[patient_card_id]=${this.patientFilter}`;
      }

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
      this.EditForm.attachment = "";
    },
    attachmentChange(e) {
      this.getAttachments();
    },
    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-attachment-delete");
    },
    deleteItem() {
      this.$store
        .dispatch("delete", `/attachment/${this.deleteForm.id}`)
        .then((response) => {
          this.getAttachments();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: `deleted ${this.deleteForm.first_name} successfully`,
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: `Error deleting ${this.deleteForm.first_name}`,
            },
          });
        });
    },
    submit() {
      this.createMode = true;
      const data = new FormData();
      console.log(this.EditForm.attachment);
      data.append("attachment", this.EditForm.attachment);
      data.append("attachable_type", "patient_card");
      data.append("type", this.selected);
      data.append("_method", "POST");
      data.append("attachable_id", this.patientCardId);

      for (const item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      if (this.createMode) {
        (this.params.form = data),
          (this.params.path = "/attachment"),
          this.$store
            .dispatch("create", this.params)
            .then((response) => {
              this.getAttachments();
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: this.$t("common.success"),
                  variant: "success",
                },
              });
            })
            .catch((error) => {
              if (error.response.status === 400) {
                for (const item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      }
    },
  },
};
</script>

<style scoped>
/* Attachments Grid */
.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0;
}

.attachment-item {
  position: relative;
}

.attachment-card {
  position: relative;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9fa;
  transition: all 0.3s ease;
  height: 100%;
}

.attachment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  border-color: #7367f0;
}

.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;
  padding: 0.25rem 0.5rem;
}

.attachment-card:hover .delete-btn {
  opacity: 1;
}

.attachment-preview {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.attachment-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.attachment-card:hover .attachment-image {
  transform: scale(1.05);
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(115, 103, 240, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
}

.attachment-card:hover .preview-overlay {
  opacity: 1;
}

/* Upload Section */
.upload-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px dashed #e9ecef;
}

.upload-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.scanner-component {
  display: inline-flex;
}

.browse-btn {
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
}

.browse-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(115, 103, 240, 0.2);
}

.selected-file-info {
  margin-top: 1rem;
  padding: 1rem;
  background: #f0f2f5;
  border-radius: 8px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .attachments-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }
  
  .upload-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .scanner-component,
  .browse-btn {
    width: 100%;
  }
}

/* Legacy Styles */
.button-preview {
  align-items: center;
  appearance: none;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.375em;
  box-shadow: none;
  box-sizing: border-box;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-family: BlinkMacSystemFont, -apple-system, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Helvetica,
    Arial, sans-serif;
  font-size: 1rem;
  height: 2.5em;
  justify-content: center;
  line-height: 1.5;
  padding: calc(0.5em - 1px) 1em;
  position: relative;
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  vertical-align: top;
  white-space: nowrap;
  margin-bottom: 3px;
  margin-right: 10px;
}

.button-preview:active {
  border-color: #4a4a4a;
  outline: 0;
}

.button-preview:focus {
  border-color: #485fc7;
  outline: 0;
}

.button-preview:hover {
  border-color: #b5b5b5;
  background: rgb(245, 241, 241);
}

.button-preview:focus:not(:active) {
  box-shadow: rgba(72, 95, 199, 0.25) 0 0 0 0.125em;
}

.modify {
  height: 6rem;
  font-size: 2rem;
  align-items: center;
}

.modify:hover {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
